import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { api } from '../services/authService'
import { apiService } from '../services/apiService'
import toast from 'react-hot-toast'

const ContractInvoicesPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [contract, setContract] = useState(null)
  const [allMonths, setAllMonths] = useState([])
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [generatingSingle, setGeneratingSingle] = useState(null)
  const [savingMonth, setSavingMonth] = useState(null)
  const [savingInvoiceId, setSavingInvoiceId] = useState(null)
  const [sendingEmailId, setSendingEmailId] = useState(null)
  const [sendEmailOnGenerate, setSendEmailOnGenerate] = useState(false)
  // Datas editadas localmente (antes de salvar): { "2026-3": "2026-03-20" }
  const [editedDates, setEditedDates] = useState({})
  // Datas salvas no banco: { "2026-3": "2026-03-20" }
  const [savedDates, setSavedDates] = useState({})

  useEffect(() => {
    loadData()
  }, [id])

  const loadData = async () => {
    setLoading(true)
    try {
      const [contractRes, invoicesRes] = await Promise.all([
        api.get(`/api/contracts/${id}`),
        api.get(`/api/invoices?contractId=${id}`)
      ])
      const c = contractRes.data.contract
      setContract(c)
      const saved = c.custom_month_due_dates || {}
      setSavedDates(saved)
      setEditedDates({})
      const invList = invoicesRes.data.data || invoicesRes.data || []
      buildMonthsTable(c, invList, saved)
    } catch (error) {
      console.error('Erro:', error)
      toast.error('Erro ao carregar dados')
    } finally {
      setLoading(false)
    }
  }

  const buildMonthsTable = (c, invList, saved) => {
    if (!c?.start_date || !c?.end_date) return
    const start = new Date(c.start_date)
    const end = new Date(c.end_date)
    const months = []
    const current = new Date(start.getFullYear(), start.getMonth(), 1)
    const endMonth = new Date(end.getFullYear(), end.getMonth(), 1)
    let idx = 1

    while (current <= endMonth) {
      const year = current.getFullYear()
      const month = current.getMonth()
      const invoice = invList.find(inv => {
        if (!inv.period_start) return false
        const parts = inv.period_start.substring(0, 10).split('-')
        return parseInt(parts[0]) === year && parseInt(parts[1]) - 1 === month
      })
      const diaVenc = c.dia_vencimento_boleto || 15
      const monthKey = `${year}-${month + 1}`
      const savedDate = saved[monthKey]
      months.push({
        idx: idx++,
        year,
        month: month + 1,
        monthKey,
        label: current.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }),
        periodStart: new Date(year, month, 1),
        periodEnd: new Date(year, month + 1, 0),
        invoice,
        defaultDueDate: toDateStr(new Date(year, month, diaVenc)),
        savedDueDate: savedDate || null
      })
      current.setMonth(current.getMonth() + 1)
    }
    setAllMonths(months)
  }

  const toDateStr = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

  const getDisplayDate = (m) => {
    if (editedDates[m.monthKey]) return editedDates[m.monthKey]
    if (savedDates[m.monthKey]) return savedDates[m.monthKey]
    return m.defaultDueDate
  }

  const isDateModified = (m) => {
    const edited = editedDates[m.monthKey]
    if (!edited) return false
    const current = savedDates[m.monthKey] || m.defaultDueDate
    return edited !== current
  }

  const handleDateChange = (m, value) => {
    setEditedDates(prev => ({ ...prev, [m.monthKey]: value }))
  }

  const handleSaveDate = async (m) => {
    const dateValue = editedDates[m.monthKey]
    if (!dateValue) return
    setSavingMonth(m.monthKey)
    try {
      await apiService.saveMonthDueDate(id, m.year, m.month, dateValue)
      setSavedDates(prev => ({ ...prev, [m.monthKey]: dateValue }))
      setEditedDates(prev => { const n = { ...prev }; delete n[m.monthKey]; return n })
      toast.success(`Vencimento de ${m.label} salvo: ${formatDate(dateValue)}`)
    } catch (error) {
      toast.error('Erro ao salvar: ' + (error.response?.data?.message || error.message))
    } finally {
      setSavingMonth(null)
    }
  }

  const handleSaveInvoiceDueDate = async (inv, newDate) => {
    if (!newDate) return
    setSavingInvoiceId(inv.id)
    try {
      const hasCharge = !!inv.asaas_charge_id
      const regenerate = hasCharge
        ? window.confirm('Esta fatura tem cobrança no Asaas.\nDeseja cancelar a atual e gerar nova com o novo vencimento?')
        : false
      await apiService.updateInvoiceDueDate(inv.id, newDate, regenerate)
      toast.success('Vencimento atualizado!')
      loadData()
    } catch (error) {
      toast.error('Erro: ' + (error.response?.data?.message || error.message))
    } finally {
      setSavingInvoiceId(null)
    }
  }

  const handleSendEmail = async (invoiceId) => {
    setSendingEmailId(invoiceId)
    try {
      await apiService.sendInvoiceEmail(invoiceId)
      toast.success('Email enviado!')
    } catch (error) {
      toast.error('Erro ao enviar email: ' + (error.response?.data?.message || error.message))
    } finally {
      setSendingEmailId(null)
    }
  }

  const handleGenerateSingle = async (m) => {
    setGeneratingSingle(m.monthKey)
    try {
      const customDate = savedDates[m.monthKey] || editedDates[m.monthKey] || null
      await apiService.generateMonthlyInvoices({
        contractIds: [id],
        months: [{ year: m.year, month: m.month, due_date: customDate }],
        sendNotification: sendEmailOnGenerate
      })
      toast.success(`Fatura de ${m.label} gerada!${sendEmailOnGenerate ? ' Email enviado.' : ''}`)
      loadData()
    } catch (error) {
      toast.error('Erro: ' + (error.response?.data?.message || error.message))
    } finally {
      setGeneratingSingle(null)
    }
  }

  const handleGenerateAll = async () => {
    const missing = allMonths.filter(m => !m.invoice)
    if (missing.length === 0) { toast('Todas as faturas já foram geradas!'); return }

    const emailMsg = sendEmailOnGenerate ? ' e enviar emails' : ''
    if (!window.confirm(`Gerar ${missing.length} fatura(s)${emailMsg}?`)) return

    setGenerating(true)
    try {
      await apiService.generateMonthlyInvoices({
        contractIds: [id],
        months: missing.map(m => ({
          year: m.year,
          month: m.month,
          due_date: savedDates[m.monthKey] || editedDates[m.monthKey] || null
        })),
        sendNotification: sendEmailOnGenerate
      })
      toast.success(`${missing.length} fatura(s) gerada(s)!${sendEmailOnGenerate ? ' Emails enviados.' : ''}`)
      loadData()
    } catch (error) {
      toast.error('Erro: ' + (error.response?.data?.message || error.message))
    } finally {
      setGenerating(false)
    }
  }

  const formatCurrency = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
  const formatDate = (d) => {
    if (!d) return '—'
    const parts = d.substring(0, 10).split('-')
    return `${parts[2]}/${parts[1]}/${parts[0]}`
  }

  const getStatusBadge = (status) => {
    const cfg = {
      PENDING: { label: 'Pendente', cls: 'bg-yellow-100 text-yellow-800' },
      PAID: { label: 'Paga', cls: 'bg-green-100 text-green-800' },
      OVERDUE: { label: 'Vencida', cls: 'bg-red-100 text-red-800' },
      CANCELLED: { label: 'Cancelada', cls: 'bg-gray-100 text-gray-800' }
    }
    const c = cfg[status] || { label: status, cls: 'bg-gray-100 text-gray-800' }
    return <span className={`px-2 py-1 rounded-full text-xs font-semibold ${c.cls}`}>{c.label}</span>
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!contract) {
    return <div className="text-center py-12 text-gray-500">Contrato não encontrado</div>
  }

  const generatedCount = allMonths.filter(m => m.invoice).length
  const missingCount = allMonths.filter(m => !m.invoice).length

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/contracts')}
            className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white text-sm"
          >
            ← Voltar
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Faturas do Contrato {contract.contract_number}/{new Date(contract.start_date).getFullYear()}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
              {contract.client_name} — {contract.vehicle_model} ({contract.license_plate})
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={sendEmailOnGenerate}
              onChange={(e) => setSendEmailOnGenerate(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Enviar email ao gerar</span>
          </label>

          {missingCount > 0 && (
            <button
              onClick={handleGenerateAll}
              disabled={generating}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-semibold flex items-center gap-2"
            >
              {generating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Gerando...
                </>
              ) : (
                <>Gerar {missingCount} Fatura(s)</>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-500">Total de Meses</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{allMonths.length}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-500">Faturas Geradas</p>
          <p className="text-2xl font-bold text-green-600">{generatedCount}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-500">Aguardando Geração</p>
          <p className="text-2xl font-bold text-orange-600">{missingCount}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-500">Valor Mensal</p>
          <p className="text-2xl font-bold text-blue-600">{formatCurrency(contract.monthly_rate)}</p>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">#</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Competência</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Período</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Vencimento</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Valor</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Status</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {allMonths.map((m) => {
                const inv = m.invoice
                const canEdit = inv && !['PAID', 'CANCELLED'].includes(inv.status)
                const modified = !inv && isDateModified(m)
                const isSaved = !inv && !!savedDates[m.monthKey] && !modified

                return (
                  <tr
                    key={m.monthKey}
                    className={`${inv ? 'hover:bg-gray-50 dark:hover:bg-gray-750' : modified ? 'bg-yellow-50/50 dark:bg-yellow-900/10' : 'bg-gray-50/50 dark:bg-gray-800/50'}`}
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                      {m.idx}/{allMonths.length}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white capitalize font-medium">
                      {m.label}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {inv ? (
                        <>{formatDate(inv.period_start)} a {formatDate(inv.period_end)}</>
                      ) : (
                        <span className="text-gray-400">
                          {m.periodStart.toLocaleDateString('pt-BR')} a {m.periodEnd.toLocaleDateString('pt-BR')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {inv ? (
                        canEdit ? (
                          <input
                            id={`due-date-${inv.id}`}
                            type="date"
                            defaultValue={inv.due_date ? inv.due_date.substring(0, 10) : ''}
                            disabled={savingInvoiceId === inv.id}
                            className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 w-40"
                          />
                        ) : (
                          <span className="text-gray-900 dark:text-white font-medium">
                            {formatDate(inv.due_date)}
                          </span>
                        )
                      ) : (
                        <div className="flex items-center gap-2">
                          <input
                            type="date"
                            value={getDisplayDate(m)}
                            onChange={(e) => handleDateChange(m, e.target.value)}
                            className={`px-2 py-1 border rounded text-sm bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 w-40 ${
                              isSaved ? 'border-green-400 dark:border-green-600' : modified ? 'border-yellow-400 dark:border-yellow-600' : 'border-dashed border-gray-300 dark:border-gray-600'
                            }`}
                          />
                          {isSaved && (
                            <span className="text-green-600 text-xs font-medium" title="Data salva no banco">
                              ✓
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white">
                      {inv ? formatCurrency(Number(inv.total_value)) : (
                        <span className="text-gray-400">{formatCurrency(Number(contract.monthly_rate))}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {inv ? getStatusBadge(inv.status) : (
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                          Não gerada
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center justify-center gap-1">
                        {inv ? (
                          <>
                            {canEdit && (
                              <button
                                onClick={() => {
                                  const input = document.querySelector(`#due-date-${inv.id}`)
                                  if (input) {
                                    const newVal = input.value
                                    const oldVal = inv.due_date ? inv.due_date.substring(0, 10) : ''
                                    if (newVal && newVal !== oldVal) handleSaveInvoiceDueDate(inv, newVal)
                                    else toast('Altere a data antes de salvar')
                                  }
                                }}
                                disabled={savingInvoiceId === inv.id}
                                className="p-1.5 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg"
                                title="Salvar vencimento"
                              >
                                {savingInvoiceId === inv.id ? (
                                  <Spinner />
                                ) : (
                                  <CheckIcon />
                                )}
                              </button>
                            )}
                            {inv.asaas_charge_id && ['PENDING', 'OVERDUE'].includes(inv.status) && (
                              <button
                                onClick={() => handleSendEmail(inv.id)}
                                disabled={sendingEmailId === inv.id}
                                className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg"
                                title="Enviar email"
                              >
                                {sendingEmailId === inv.id ? <Spinner /> : <EmailIcon />}
                              </button>
                            )}
                            {inv.pdf_url && (
                              <a
                                href={inv.pdf_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1.5 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/30 rounded-lg"
                                title="Ver fatura PDF"
                              >
                                <InvoiceIcon />
                              </a>
                            )}
                            {inv.asaas_payment_url && ['PENDING', 'OVERDUE'].includes(inv.status) && (
                              <a
                                href={inv.asaas_payment_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                                title="Link de pagamento (Asaas)"
                              >
                                <LinkIcon />
                              </a>
                            )}
                            {inv.boleto_url && (
                              <a
                                href={inv.boleto_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1.5 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-lg"
                                title="Ver boleto PDF"
                              >
                                <DocIcon />
                              </a>
                            )}
                          </>
                        ) : (
                          <div className="flex items-center gap-1">
                            {modified && (
                              <button
                                onClick={() => handleSaveDate(m)}
                                disabled={savingMonth === m.monthKey}
                                className="px-2 py-1.5 bg-green-600 text-white rounded-lg text-xs font-semibold hover:bg-green-700 disabled:opacity-50"
                                title="Salvar data de vencimento"
                              >
                                {savingMonth === m.monthKey ? '...' : 'Salvar'}
                              </button>
                            )}
                            <button
                              onClick={() => handleGenerateSingle(m)}
                              disabled={generatingSingle === m.monthKey}
                              className="px-2 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 disabled:opacity-50"
                            >
                              {generatingSingle === m.monthKey ? '...' : 'Gerar'}
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// Mini SVG icons
const Spinner = () => <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent"></div>
const CheckIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
)
const EmailIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
)
const LinkIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
)
const InvoiceIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
)
const DocIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
)

export default ContractInvoicesPage
